/*
 * MON VERRE
 * Custom Gift With Purchase
 *
 * - One combined shipping + GWP progress bar
 * - AUD / USD gift thresholds
 * - Dynamic free-shipping milestone
 * - Auto add/remove gift
 * - Gift excluded from qualifying subtotal
 * - Duplicate protection
 * - Gift quantity locked to 1
 * - Palo Alto drawer refresh support
 * - Zepto personalised-product support
 */

(function () {
  if (window.customMonVerreGwpInitialized) return;
  window.customMonVerreGwpInitialized = true;

  class CustomMonVerreGwp {
    constructor() {
      this.config = window.themeGwp || {};

      this.enabled = Boolean(this.config.enabled);
      this.variantId = Number(this.config.variantId || 0);
      this.variantCount = Number(this.config.variantCount || 0);
      this.giftPrice = Number(this.config.giftPrice || 0);

      this.productHandle = String(
        this.config.productHandle || ''
      );

      this.thresholdAUD = Number(
        this.config.thresholdAUD || 0
      );

      this.thresholdUSD = Number(
        this.config.thresholdUSD || 0
      );

      this.progressMessage =
        this.config.progressMessage ||
        'Spend [amount] more to unlock your FREE gift.';

      this.unlockedMessage =
        this.config.unlockedMessage ||
        "You've unlocked your FREE gift!";

      this.badgeText =
        this.config.badgeText ||
        'FREE GIFT';

      this.isProcessing = false;
      this.updateTimer = null;
      this.retryGiftAfter = 0;
      this.cartDrawerObserver = null;

      this.init();
    }


    /* =========================================
       INIT
       ========================================= */

    init() {
      this.bindEvents();
      this.patchFetch();
      this.observeCartDrawer();

      this.scheduleUpdate(100);
      this.scheduleUpdate(1000);
    }


    /* =========================================
       ROUTES
       ========================================= */

    getRoute(path) {
      const root =
        window.Shopify?.routes?.root || '/';

      return `${root}${path}`.replace(
        /\/+/g,
        '/'
      );
    }


    /* =========================================
       CONFIG VALIDATION
       ========================================= */

    isGiftConfigurationValid() {
      if (!this.variantId) {
        console.warn(
          'Custom GWP: No gift product selected.'
        );
        return false;
      }

      if (this.variantCount !== 1) {
        console.warn(
          'Custom GWP: Gift product must have exactly one variant.'
        );
        return false;
      }

      if (this.giftPrice !== 0) {
        console.warn(
          'Custom GWP: Gift product must have a price of $0.00.'
        );
        return false;
      }

      return true;
    }


    /* =========================================
       CURRENCY / GIFT THRESHOLD
       ========================================= */

    getThreshold(currency) {
      const code =
        String(currency || '').toUpperCase();

      if (code === 'AUD') {
        return this.thresholdAUD;
      }

      if (code === 'USD') {
        return this.thresholdUSD;
      }

      return 0;
    }


    /* =========================================
       CART
       ========================================= */

    async getCart() {
      const response = await fetch(
        this.getRoute('cart.js'),
        {
          headers: {
            Accept: 'application/json'
          },
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error(
          'Custom GWP: Could not fetch cart.'
        );
      }

      return response.json();
    }


    isCustomGift(item) {
      return Boolean(
        item?.properties &&
        item.properties._custom_gwp ===
          'mon-verre'
      );
    }


    isCurrentGift(item) {
      if (!item) return false;

      return (
        Number(item.variant_id) ===
          this.variantId &&
        this.isCustomGift(item)
      );
    }


    getCustomGiftItems(cart) {
      return (cart.items || []).filter(
        (item) => this.isCustomGift(item)
      );
    }


    getCurrentGiftItems(cart) {
      return (cart.items || []).filter(
        (item) => this.isCurrentGift(item)
      );
    }


    getQualifyingSubtotal(cart) {
      return (cart.items || []).reduce(
        (total, item) => {
          if (this.isCustomGift(item)) {
            return total;
          }

          return (
            total +
            Number(item.final_line_price || 0)
          );
        },
        0
      );
    }


    /* =========================================
       EVENTS
       ========================================= */

    bindEvents() {
      const refreshEvents = [
        'theme:cart:refresh',
        'theme:cart:reload',
        'theme:cart:update',
        'theme:cart:change',
        'cart:refresh',
        'cart:updated',
        'cart:change',
        'cart:update',
        'ajaxProduct:added',
        'product:added',
        'pplrAddToCartCompleted'
      ];

      refreshEvents.forEach((eventName) => {
        document.addEventListener(
          eventName,
          () => {
            this.scheduleUpdate(500);
          }
        );
      });


      document.addEventListener(
        'click',
        (event) => {
          if (
            event.target.closest(
              '[data-quantity-plus]'
            ) ||
            event.target.closest(
              '[data-quantity-minus]'
            ) ||
            event.target.closest(
              '[data-item-remove]'
            ) ||
            event.target.closest(
              '[data-remove-item]'
            ) ||
            event.target.closest(
              '[data-cart-item-remove]'
            ) ||
            event.target.closest(
              'quantity-input'
            ) ||
            event.target.closest(
              'quantity-counter'
            ) ||
            event.target.closest(
              '[name="add"]'
            )
          ) {
            this.scheduleUpdate(800);
          }
        }
      );


      document.addEventListener(
        'change',
        (event) => {
          if (
            event.target.matches(
              'input[name="updates[]"]'
            ) ||
            event.target.matches(
              '[data-quantity-input]'
            ) ||
            event.target.closest(
              'quantity-input'
            ) ||
            event.target.closest(
              'quantity-counter'
            )
          ) {
            this.scheduleUpdate(800);
          }
        }
      );
    }


    /* =========================================
       PALO ALTO DRAWER OBSERVER
       ========================================= */

    observeCartDrawer() {
      const cartDrawer =
        document.querySelector(
          '[data-cart-drawer]'
        );

      if (!cartDrawer) return;

      const observer =
        new MutationObserver(
          (mutations) => {
            const relevantMarkupAdded =
              mutations.some(
                (mutation) =>
                  Array.from(
                    mutation.addedNodes
                  ).some((node) => {
                    if (
                      node.nodeType !==
                      Node.ELEMENT_NODE
                    ) {
                      return false;
                    }

                    return Boolean(
                      node.matches?.(
                        '[data-custom-gwp-progress], [data-cart-item]'
                      ) ||
                      node.querySelector?.(
                        '[data-custom-gwp-progress], [data-cart-item]'
                      )
                    );
                  })
              );

            if (relevantMarkupAdded) {
              this.scheduleUpdate(150);
            }
          }
        );

      observer.observe(
        cartDrawer,
        {
          childList: true,
          subtree: true
        }
      );

      this.cartDrawerObserver =
        observer;
    }


    /* =========================================
       FETCH WATCHER
       ========================================= */

    patchFetch() {
      if (
        window.customMonVerreGwpFetchPatched
      ) {
        return;
      }

      window.customMonVerreGwpFetchPatched =
        true;

      const originalFetch =
        window.fetch;

      const self = this;

      window.fetch = async function () {
        const response =
          await originalFetch.apply(
            this,
            arguments
          );

        try {
          const input =
            arguments[0];

          const url =
            typeof input === 'string'
              ? input
              : input?.url || '';

          if (
            self.isCartMutationUrl(url)
          ) {
            self.scheduleUpdate(450);
          }
        } catch (error) {}

        return response;
      };
    }


    isCartMutationUrl(url) {
      return (
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      );
    }


    scheduleUpdate(delay = 300) {
      clearTimeout(this.updateTimer);

      this.updateTimer =
        setTimeout(() => {
          this.sync();
        }, delay);
    }


    /* =========================================
       MAIN SYNC
       ========================================= */

    async sync() {
      if (this.isProcessing) return;

      this.isProcessing = true;

      try {
        let cart =
          await this.getCart();

        const currency =
          String(
            cart.currency ||
            window.Shopify?.currency?.active ||
            ''
          ).toUpperCase();

        const threshold =
          this.getThreshold(currency);

        let customGiftItems =
          this.getCustomGiftItems(cart);


        /* GWP disabled */

        if (!this.enabled) {
          if (customGiftItems.length) {
            await this.removeGiftItems(
              customGiftItems
            );

            await this.refreshThemeCart();

            cart =
              await this.getCart();
          }

          this.updateProgressUI(
            cart,
            0,
            currency,
            false
          );

          return;
        }


        /* Remove old gift if selected product changes */

        const oldGiftItems =
          customGiftItems.filter(
            (item) =>
              Number(item.variant_id) !==
              this.variantId
          );

        if (oldGiftItems.length) {
          await this.removeGiftItems(
            oldGiftItems
          );

          await this.refreshThemeCart();

          cart =
            await this.getCart();
        }


        /* Invalid gift setup */

        if (
          !this.isGiftConfigurationValid()
        ) {
          const existingGift =
            this.getCurrentGiftItems(cart);

          if (existingGift.length) {
            await this.removeGiftItems(
              existingGift
            );

            await this.refreshThemeCart();

            cart =
              await this.getCart();
          }

          this.updateProgressUI(
            cart,
            threshold,
            currency,
            false
          );

          return;
        }


        /* Unsupported currency */

        if (!threshold) {
          const existingGift =
            this.getCurrentGiftItems(cart);

          if (existingGift.length) {
            await this.removeGiftItems(
              existingGift
            );

            await this.refreshThemeCart();

            cart =
              await this.getCart();
          }

          this.updateProgressUI(
            cart,
            0,
            currency,
            false
          );

          return;
        }


        /* Remove duplicates */

        let currentGiftItems =
          this.getCurrentGiftItems(cart);

        if (
          currentGiftItems.length > 1
        ) {
          await this.removeGiftItems(
            currentGiftItems.slice(1)
          );

          await this.refreshThemeCart();

          cart =
            await this.getCart();

          currentGiftItems =
            this.getCurrentGiftItems(
              cart
            );
        }


        /* Force gift quantity to one */

        const giftItem =
          currentGiftItems[0];

        if (
          giftItem &&
          Number(giftItem.quantity) !== 1
        ) {
          await this.changeItem(
            giftItem.key,
            1
          );

          await this.refreshThemeCart();

          cart =
            await this.getCart();

          currentGiftItems =
            this.getCurrentGiftItems(
              cart
            );
        }


        let subtotal =
          this.getQualifyingSubtotal(cart);

        let currentGift =
          currentGiftItems[0];


        /* Add gift */

        if (
          subtotal >= threshold &&
          !currentGift &&
          Date.now() >=
            this.retryGiftAfter
        ) {
          const added =
            await this.addGift();

          if (added) {
            await this.refreshThemeCart();

            cart =
              await this.getCart();

            subtotal =
              this.getQualifyingSubtotal(
                cart
              );

            currentGift =
              this.getCurrentGiftItems(
                cart
              )[0];
          }
        }


        /* Remove gift */

        if (
          subtotal < threshold &&
          currentGift
        ) {
          await this.removeGiftItems(
            this.getCurrentGiftItems(cart)
          );

          await this.refreshThemeCart();

          cart =
            await this.getCart();
        }


        this.updateProgressUI(
          cart,
          threshold,
          currency,
          true
        );


        setTimeout(() => {
          this.markGiftLineInDom();
        }, 150);

      } catch (error) {
        console.error(
          'Custom GWP:',
          error
        );

      } finally {
        this.isProcessing = false;
      }
    }


    /* =========================================
       ADD GIFT
       ========================================= */

    async addGift() {
      try {
        const response = await fetch(
          this.getRoute('cart/add.js'),
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json'
            },

            body: JSON.stringify({
              items: [
                {
                  id: this.variantId,
                  quantity: 1,

                  properties: {
                    _free_gift: 'true',
                    _custom_gwp:
                      'mon-verre'
                  }
                }
              ]
            })
          }
        );

        if (!response.ok) {
          let errorData = {};

          try {
            errorData =
              await response.json();
          } catch (error) {}

          console.warn(
            'Custom GWP: Gift could not be added.',
            errorData
          );

          this.retryGiftAfter =
            Date.now() + 30000;

          return false;
        }

        return true;

      } catch (error) {
        console.error(
          'Custom GWP: Failed adding gift.',
          error
        );

        this.retryGiftAfter =
          Date.now() + 30000;

        return false;
      }
    }


    /* =========================================
       REMOVE GIFTS
       ========================================= */

    async removeGiftItems(items) {
      if (!items?.length) return;

      const updates = {};

      items.forEach((item) => {
        if (item?.key) {
          updates[item.key] = 0;
        }
      });

      if (
        !Object.keys(updates).length
      ) {
        return;
      }

      const response = await fetch(
        this.getRoute('cart/update.js'),
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },

          body: JSON.stringify({
            updates
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Custom GWP: Failed removing gift.'
        );
      }
    }


    /* =========================================
       CHANGE GIFT QUANTITY
       ========================================= */

    async changeItem(
      key,
      quantity
    ) {
      const response = await fetch(
        this.getRoute('cart/change.js'),
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },

          body: JSON.stringify({
            id: key,
            quantity:
              Number(quantity)
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Custom GWP: Failed changing gift quantity.'
        );
      }
    }


    /* =========================================
       PALO ALTO CART REFRESH
       ========================================= */

    async refreshThemeCart() {
      const cartItems =
        document.querySelector(
          'cart-items'
        );

      if (
        cartItems &&
        typeof cartItems.getCart ===
          'function'
      ) {
        try {
          await cartItems.getCart();

        } catch (error) {
          console.warn(
            'Custom GWP: Palo Alto cart refresh failed.',
            error
          );
        }
      }

      document.dispatchEvent(
        new CustomEvent(
          'theme:cart:refresh',
          {
            bubbles: true
          }
        )
      );
    }


    /* =========================================
       MONEY
       ========================================= */

    formatMoney(
    cents,
    currency
    ) {
    const amount =
        Number(cents || 0) / 100;

    const currencyCode =
        String(currency || '').toUpperCase();

    try {
        const formatted =
        new Intl.NumberFormat(
            document.documentElement.lang || 'en',
            {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
            }
        ).format(amount);

        return `${formatted} ${currencyCode}`;

    } catch (error) {
        return `$${amount.toFixed(2)} ${currencyCode}`;
    }
    }


    /* =========================================
       COMBINED SHIPPING + GWP PROGRESS
       ========================================= */

    updateProgressUI(
      cart,
      threshold,
      currency,
      show
    ) {
      const components =
        document.querySelectorAll(
          '[data-custom-gwp-progress]'
        );

      if (!components.length) {
        return;
      }

      const subtotal =
        this.getQualifyingSubtotal(
          cart
        );

      components.forEach(
        (component) => {
          if (
            !show ||
            !threshold ||
            !currency
          ) {
            component.hidden = true;
            return;
          }

          component.hidden = false;

          const shippingThreshold =
            Number(
              component.dataset
                .shippingThreshold || 0
            );

          const message =
            component.querySelector(
              '[data-custom-gwp-message]'
            );

          const fill =
            component.querySelector(
              '[data-custom-gwp-bar]'
            );

          const shippingMilestone =
            component.querySelector(
              '[data-custom-gwp-shipping-milestone]'
            );

          const giftMilestone =
            component.querySelector(
              '.custom-gwp-rewards__milestone--gift'
            );


          /*
           * Entire bar represents progress
           * from $0 to the GWP threshold.
           */
          const progress =
            Math.min(
              Math.max(
                (subtotal / threshold) *
                  100,
                0
              ),
              100
            );


          if (fill) {
            fill.style.width =
              `${progress}%`;
          }


          /*
           * Position free-shipping icon based
           * on its actual threshold.
           *
           * AUD example:
           * $80 / $200 = 40%
           *
           * USD example:
           * $80 / $250 = 32%
           */
          if (
            shippingMilestone &&
            shippingThreshold > 0
          ) {
            const shippingPosition =
              Math.min(
                Math.max(
                  (
                    shippingThreshold /
                    threshold
                  ) * 100,
                  0
                ),
                100
              );

            shippingMilestone.style.left =
              `${shippingPosition}%`;


            shippingMilestone.classList.toggle(
              'is-unlocked',
              subtotal >= shippingThreshold
            );
          }


          if (giftMilestone) {
            giftMilestone.classList.toggle(
              'is-unlocked',
              subtotal >= threshold
            );
          }


          /*
           * MESSAGE STAGE 1:
           * progress toward free shipping.
           */
          if (
            shippingThreshold > 0 &&
            subtotal < shippingThreshold
          ) {
            const remainingShipping =
              shippingThreshold - subtotal;

            if (message) {
              message.textContent =
                `Spend ${this.formatMoney(
                  remainingShipping,
                  currency
                )} more for FREE shipping.`;
            }

            component.classList.remove(
              'is-unlocked'
            );

            return;
          }


          /*
           * MESSAGE STAGE 2:
           * free shipping achieved,
           * progress toward gift.
           */
          if (subtotal < threshold) {
            const remainingGift =
              threshold - subtotal;

            if (message) {
              message.textContent =
                this.progressMessage.replace(
                  '[amount]',
                  this.formatMoney(
                    remainingGift,
                    currency
                  )
                );
            }

            component.classList.remove(
              'is-unlocked'
            );

            return;
          }


          /*
           * MESSAGE STAGE 3:
           * gift unlocked.
           */
          if (message) {
            message.textContent =
              this.unlockedMessage;
          }

          component.classList.add(
            'is-unlocked'
          );
        }
      );
    }


    /* =========================================
       FREE GIFT CART LINE
       ========================================= */

    markGiftLineInDom() {
      if (!this.productHandle) {
        return;
      }

      document
        .querySelectorAll(
          '.custom-gwp-gift-item'
        )
        .forEach((row) => {
          row.classList.remove(
            'custom-gwp-gift-item'
          );

          row.removeAttribute(
            'data-custom-gwp-gift-item'
          );
        });


      const links =
        document.querySelectorAll(
          'a[href*="/products/"]'
        );


      links.forEach((link) => {
        try {
          const url =
            new URL(
              link.href,
              window.location.origin
            );

          const path =
            url.pathname.replace(
              /\/$/,
              ''
            );

          if (
            path !==
            `/products/${this.productHandle}`
          ) {
            return;
          }


          /*
            * The GWP product is not intended to be
            * browsed as a normal product.
            *
            * Redirect both its image and title links
            * to the storefront homepage.
            */
            link.href =
            window.Shopify?.routes?.root || '/';

            link.removeAttribute('target');


          const row =
            link.closest(
              '.cart__item'
            ) ||
            link.closest(
              '.cart-item'
            ) ||
            link.closest(
              '[data-cart-item]'
            ) ||
            link.closest(
              '.line-item'
            ) ||
            link.closest('li');

          if (!row) return;


          row.classList.add(
            'custom-gwp-gift-item'
          );

          row.setAttribute(
            'data-custom-gwp-gift-item',
            'true'
          );


          if (
            !row.querySelector(
              '.custom-gwp-gift-badge'
            )
          ) {
            const badge =
              document.createElement(
                'span'
              );

            badge.className =
              'custom-gwp-gift-badge';

            badge.textContent =
              this.badgeText;


            const title =
              row.querySelector(
                '.cart__item__title, .cart-item__title, [data-cart-item-title]'
              );


            if (title) {
              title.insertAdjacentElement(
                'afterend',
                badge
              );
            }
          }

        } catch (error) {}
      });
    }
  }


  /* =========================================
     START
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        window.customMonVerreGwp =
          new CustomMonVerreGwp();
      }
    );

  } else {
    window.customMonVerreGwp =
      new CustomMonVerreGwp();
  }
})();